/**
 * „Passen die offenen Positionen noch zum Konto?"
 *
 * ANLASS (10.08.2026). Das Konto stand bis heute auf 1.000.000 € — ein
 * Testwert. Die echte Größe ist 10.000 €. Vier reale Positionen wurden für
 * die Million dimensioniert; auf zehntausend wären sie ein Vielfaches des
 * Kontos. Nichts in der App hätte das gemeldet: Positionsgrößen werden beim
 * Anlegen gegen die damalige Kontogröße gerechnet und danach nie wieder.
 *
 * Genau das ist die Lücke. Eine Kontogröße ist keine Konstante — sie ändert
 * sich, und alte Positionen ändern sich nicht mit. Diese Prüfung läuft
 * deshalb gegen den AKTUELLEN Stand, nicht gegen den zum Zeitpunkt des
 * Einstiegs.
 *
 * WAS GERECHNET UND WAS GESETZT IST — die Unterscheidung ist wichtig, weil
 * dieses Projekt sich sonst selbst belügt:
 *
 *   Gerechnet (reine Arithmetik, keine Meinung):
 *     - Überzeichnung: Positionswert > Equity. Das ist kein Schwellenwert,
 *       das ist eine Subtraktion. Man kann nicht mehr halten als man hat.
 *
 *   Aus DEINEN Einstellungen (nicht von mir erfunden):
 *     - max_total_open_risk_pct (Standard 5) — deine eigene Obergrenze für
 *       die Summe des offenen Risikos.
 *
 *   Konvention, also Ermessen (im Text als solche kenntlich gemacht):
 *     - RESERVE_WARNUNG_PCT und KLUMPEN_ANTEIL. Beide sind Faustregeln, für
 *       die es in diesem Projekt KEINE Messung gibt. Sie stehen hier als
 *       Konstante, damit sie sichtbar und änderbar sind statt im Fließtext
 *       versteckt.
 *
 * Kein Befund ist ein vollwertiges Ergebnis: Die Funktion gibt dann eine
 * leere Liste zurück, und die Karte zeigt nichts. Eine Warnung, die immer
 * da ist, ist keine Warnung mehr.
 */

import type { AccountSummary } from '@/lib/paper-account';

/** Ab welchem Investitionsgrad die Reserve knapp wird. Konvention. */
export const RESERVE_WARNUNG_PCT = 90;

/** Ab welchem Anteil einer einzelnen Position von Klumpenrisiko die Rede ist. Konvention. */
export const KLUMPEN_ANTEIL = 0.5;

export type BefundArt =
  | 'ueberzeichnet'
  | 'reserve_knapp'
  | 'risikosumme_ueber_grenze'
  | 'klumpen';

export interface KapitalBefund {
  art: BefundArt;
  /** true = Arithmetik, false = Faustregel. Steuert den Ton in der Anzeige. */
  gerechnet: boolean;
  satz: string;
  erklaerung: string;
}

export interface KapitalPruefEingabe {
  summary: AccountSummary;
  /** Deine Obergrenze aus den Einstellungen (max_total_open_risk_pct). */
  maxOffenesRisikoPct: number;
}

const geld = (n: number, ccy: string): string =>
  `${n.toLocaleString('de-DE', { maximumFractionDigits: 0 })} ${ccy}`;

export function pruefeKapital({
  summary: s,
  maxOffenesRisikoPct,
}: KapitalPruefEingabe): KapitalBefund[] {
  const befunde: KapitalBefund[] = [];

  // Ohne offene Positionen gibt es nichts zu prüfen. Ohne belastbare Equity
  // auch nicht — eine Warnung auf Basis einer 0-Equity wäre Rauschen.
  if (s.open_count === 0 || !Number.isFinite(s.equity) || s.equity <= 0) return befunde;

  const ccy = s.account_currency;

  // ---------------------------------------------------------------------
  // 1. Überzeichnung — der eigentliche Anlass. Reine Subtraktion.
  // ---------------------------------------------------------------------
  if (s.invested_capital > s.equity) {
    const fehlt = s.invested_capital - s.equity;
    const faktor = s.invested_capital / s.equity;
    befunde.push({
      art: 'ueberzeichnet',
      gerechnet: true,
      satz:
        `Offene Positionen binden ${geld(s.invested_capital, ccy)} — `
        + `das Konto trägt ${geld(s.equity, ccy)}.`,
      erklaerung:
        `Es fehlen ${geld(fehlt, ccy)}; die Positionen sind zusammen das `
        + `${faktor.toFixed(1)}-fache des Kontos. Diese Größen stammen aus einer `
        + `Zeit, in der das Konto größer war — sie sind beim Anlegen gegen die `
        + `damalige Kontogröße gerechnet worden und seitdem nie wieder. `
        + `Das ist keine Ermessensfrage, sondern eine Subtraktion.`,
    });
  } else if (s.invested_pct >= RESERVE_WARNUNG_PCT) {
    // Nur wenn NICHT überzeichnet — sonst zwei Meldungen zur selben Sache.
    befunde.push({
      art: 'reserve_knapp',
      gerechnet: false,
      satz: `${s.invested_pct.toFixed(0)} % des Kontos stecken in Positionen.`,
      erklaerung:
        `Frei sind noch ${geld(s.free_cash, ccy)}. Ein neues Signal ließe sich `
        + `kaum noch in sinnvoller Größe nachbilden. `
        + `Die Grenze von ${RESERVE_WARNUNG_PCT} % ist eine Faustregel, keine Messung.`,
    });
  }

  // ---------------------------------------------------------------------
  // 2. Risikosumme gegen DEINE eigene Obergrenze.
  // ---------------------------------------------------------------------
  if (
    Number.isFinite(maxOffenesRisikoPct)
    && maxOffenesRisikoPct > 0
    && s.total_open_risk_pct > maxOffenesRisikoPct
  ) {
    befunde.push({
      art: 'risikosumme_ueber_grenze',
      gerechnet: true,
      satz:
        `Offenes Risiko ${s.total_open_risk_pct.toFixed(1)} % — deine Grenze `
        + `liegt bei ${maxOffenesRisikoPct} %.`,
      erklaerung:
        `Wenn alle offenen Positionen auf ihren Stop laufen, kostet das `
        + `${geld(s.total_open_risk, ccy)}. Die Grenze steht in deinen `
        + `Einstellungen (max_total_open_risk_pct); sie ist nicht von mir gesetzt.`,
    });
  }

  // ---------------------------------------------------------------------
  // 3. Klumpenrisiko. Faustregel, und nur ab zwei Positionen sinnvoll —
  //    bei einer einzigen ist der Anteil zwangsläufig 100 %.
  // ---------------------------------------------------------------------
  if (
    s.open_count >= 2
    && s.concentration_top1 !== null
    && s.concentration_top1 > KLUMPEN_ANTEIL
  ) {
    befunde.push({
      art: 'klumpen',
      gerechnet: false,
      satz:
        `Eine Position stellt ${(s.concentration_top1 * 100).toFixed(0)} % `
        + `des investierten Kapitals.`,
      erklaerung:
        `${geld(s.biggest_position_value, ccy)} von ${geld(s.invested_capital, ccy)} `
        + `hängen an einem einzigen Wert. Der Anteil von ${(KLUMPEN_ANTEIL * 100).toFixed(0)} % `
        + `ist eine Faustregel — dieses Projekt hat nicht gemessen, ab wann `
        + `Konzentration schadet.`,
    });
  }

  return befunde;
}
